import has from "lodash/has";
import mapValues from "lodash/mapValues";
import {
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_PRIVACY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { FormSchemaField, RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { isActivityFieldPermitted } from "PFCore/helpers/activity_fields_permissions";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { AccessLevel } from "PFTypes";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useShallow } from "zustand/react/shallow";

import { parseValue } from "../tabular_roles_form.utils";

export type CellBehavior = {
  isCopyPasteBlocked: boolean;
  isClearBlocked: boolean;
  onClear?: (rowOriginal: { uniqueId: string }, rowIndex: number) => void;
  onPaste?: (rowOriginal: { uniqueId: string }, rowIndex: number, rawValue: string, path: string) => void;
};

export const useGetCellBehavior = (rolesFormKey: RolesFormKey) => {
  const { setValue } = useFormContext();
  const { data: currentProfile } = useCurrentProfile();
  const activityCreators = useMultiActivityFormStore(
    useShallow((store) => mapValues(store.roles, (role) => role.activityCreator))
  );
  const setRoleData = useMultiActivityFormStore((store) => store.actions.setRoleData);
  const { customTypes } = useCustomTypes();

  return useCallback(
    (rowOriginal: { uniqueId: string }, schemaField: FormSchemaField): CellBehavior => {
      const { property } = schemaField;

      if (property?.type === "custom_field") {
        const customType = customTypes.find((ct) => ct.name === property.name) ?? null;

        if (customType) {
          const { uniqueId } = rowOriginal;
          const activityCreator = activityCreators[uniqueId] ?? null;
          const level = activityCustomTypesAccessLevels(currentProfile, activityCreator)[customType.id];

          if (level !== AccessLevel.ReadWrite) {
            return { isCopyPasteBlocked: true, isClearBlocked: true };
          }
        }
      }

      if (schemaField.name === FIELD_BOOKING_CATEGORY_ID_KEY) {
        const { uniqueId } = rowOriginal;
        const activityCreator = activityCreators[uniqueId] ?? null;
        const isReadWritePermitted =
          !!currentProfile &&
          isActivityFieldPermitted({
            currentProfile,
            activityCreator,
            fieldName: "booking_category",
            accessLevel: AccessLevel.ReadWrite
          });

        if (!isReadWritePermitted) {
          return { isCopyPasteBlocked: true, isClearBlocked: true };
        }
      }

      if (schemaField.name === FIELD_ATTACHMENT_IDS_KEY) {
        return {
          isCopyPasteBlocked: true,
          isClearBlocked: false,
          onClear: ({ uniqueId }) => setRoleData(uniqueId, { attachments: [] })
        };
      }

      if (schemaField.name === FIELD_PRIVACY_KEY) {
        const onClear = (rowIndex: number) => {
          setValue(`${rolesFormKey}.${rowIndex}.${FIELD_PRIVACY_KEY}`, null, { shouldDirty: true });
          setValue(`${rolesFormKey}.${rowIndex}.${FIELD_AUDIENCES_KEY}`, [], { shouldDirty: true });
        };
        return {
          isCopyPasteBlocked: false,
          isClearBlocked: false,
          onClear: (_, rowIndex) => onClear(rowIndex),
          onPaste: (_, rowIndex, rawValue, path) => {
            const parsed = parseValue(schemaField, rawValue);
            if (parsed === null || !has(parsed, "private") || !has(parsed, "audiences")) {
              onClear(rowIndex);
              return;
            }
            const { private: isPrivate, audiences } = parsed;
            setValue(path, isPrivate, { shouldDirty: true });
            setValue(`${rolesFormKey}.${rowIndex}.${FIELD_AUDIENCES_KEY}`, audiences, {
              shouldDirty: true
            });
          }
        };
      }

      return { isCopyPasteBlocked: false, isClearBlocked: false };
    },
    [currentProfile, activityCreators, customTypes, setRoleData, setValue, rolesFormKey]
  );
};
